import axios from 'axios';
import Router from 'next/router';
import isEmpty from 'lodash/isEmpty';
import difference from 'lodash/difference';
import { ReactPixel, generateFilterUrl, generateHistoryUrl, splitSizes, track } from '../../helpers';
import {
  FILTER_TYPE_CATEGORIES,
  FILTER_TYPE_CLEAR_ALL,
  FILTER_TYPE_DESIGNER,
  FILTER_TYPE_SEASONS,
  FILTER_TYPE_SIZES
} from '../../helpers/trackingStringValues';
import { API_RESULT } from '../../modules/api';
import { designerMode } from '../../helpers/productListModes';
import { isShoesCategory } from '../../helpers/search_helpers';
import TrackingEvents from '../models/TrackingEvents';
import { getSeasonsDisplayableForm } from '../models/SeasonData';

const notValue = newValue => !newValue || isEmpty(newValue);
const isMultipleFilterAndRemoved = (newValue, oldValue) => Array.isArray(newValue) && newValue.length < oldValue.length;

function trackFilterChange(filterType, newValue, oldValue) {
  if (notValue(newValue) || isMultipleFilterAndRemoved(newValue, oldValue) || filterType === 'all') {
    const value = Array.isArray(newValue) ? difference(oldValue, newValue)[0] : oldValue;
    track(TrackingEvents.filterRemoved(), { ui: { filter_type: filterType, value } });
  } else {
    const value = Array.isArray(newValue) ? difference(newValue, oldValue)[0] : newValue;
    track(TrackingEvents.filterApplied(), { ui: { filter_type: filterType, value } });
  }
}

function trackSeasonFilter(seasonsSelected, currentSeason, availableSeasons, oldSeasons) {
  if (!seasonsSelected || isEmpty(seasonsSelected) || seasonsSelected.length < oldSeasons.length) {
    const results = getSeasonsDisplayableForm(oldSeasons, currentSeason, availableSeasons);
    track(TrackingEvents.filterRemoved(), { ui: { filter_type: FILTER_TYPE_SEASONS, value: results } });
  } else {
    const results = getSeasonsDisplayableForm(seasonsSelected, currentSeason, availableSeasons);
    track(TrackingEvents.filterApplied(), { ui: { filter_type: FILTER_TYPE_SEASONS, value: results } });
  }
}

function fetchSuccessAction(response) {
  return { payload: response, type: API_RESULT };
}

function callBackend(url, productListMode, backUrl = null) {
  return (dispatch, getState) =>
    axios.get(url).then(response => {
      const { data } = response;
      const { fbPixelSearchDetailData } = data;
      ReactPixel.track('ViewContent', fbPixelSearchDetailData);
      dispatch(fetchSuccessAction(data));
      const { countryCode } = getState();
      const { as, queryString, page } = generateHistoryUrl(url.lastIndexOf('?') !== -1, productListMode, countryCode);
      Router.push(`/${page}${queryString}`, backUrl || as, { shallow: true });
    });
}

function filterBrands(selectedBrandsIds, productListMode) {
  return (dispatch, getState) => {
    const {
      currentCategoryId,
      seasons,
      brandsIds,
      availableBrands,
      sizes,
      storeName,
      storeId
    } = getState().api.filterSettings;
    const { sortFilterSelect, department } = getState().api;

    const oldBrandNames = availableBrands.filter(brand => brandsIds.includes(brand.id)).map(brand => brand.name);

    const newBrandNames = availableBrands
      .filter(brand => selectedBrandsIds.includes(brand.id))
      .map(brand => brand.name);

    trackFilterChange(FILTER_TYPE_DESIGNER, newBrandNames, oldBrandNames);
    const url = generateFilterUrl({
      brandsIds: selectedBrandsIds,
      department,
      categoryId: currentCategoryId,
      seasons,
      sizes,
      sortKey: sortFilterSelect,
      productListMode,
      storeName,
      storeId
    });
    dispatch(callBackend(url, productListMode));
  };
}

function filterCategories(categoryId, categoryUrl, category, productListMode) {
  return (dispatch, getState) => {
    const { brandsIds, categoryId: oldCategoryId, seasons, sizes, storeName, storeId } = getState().api.filterSettings;
    const { sortFilterSelect } = getState().api;

    let finalSizes = sizes;
    if (!shouldPreserveSizes(categoryId, oldCategoryId)) {
      finalSizes = [];
    }

    trackFilterChange(FILTER_TYPE_CATEGORIES, category);
    const url = generateFilterUrl({
      brandsIds,
      department: category.toLowerCase(),
      categoryId,
      seasons,
      sizes: finalSizes,
      sortKey: sortFilterSelect,
      productListMode,
      storeName,
      storeId
    });
    dispatch(callBackend(url, productListMode));
  };
}

function filterSeasons(seasonsSelected, productListMode) {
  return (dispatch, getState) => {
    const {
      availableSeasons,
      brandsIds,
      currentSeason,
      currentCategoryId,
      seasons: oldSeasons,
      sizes,
      storeName,
      storeId
    } = getState().api.filterSettings;
    const { sortFilterSelect, department } = getState().api;
    const url = generateFilterUrl({
      brandsIds,
      department,
      categoryId: currentCategoryId,
      seasons: seasonsSelected,
      sizes,
      sortKey: sortFilterSelect,
      productListMode,
      storeName,
      storeId
    });

    trackSeasonFilter(seasonsSelected, currentSeason, availableSeasons, oldSeasons);
    dispatch(callBackend(url, productListMode));
  };
}

function filterSizes(sizesSelected, productListMode) {
  return (dispatch, getState) => {
    const {
      brandsIds,
      currentCategoryId,
      seasons,
      sizes: oldSizes,
      storeName,
      storeId
    } = getState().api.filterSettings;
    const { department } = getState().api;
    const { sortFilterSelect } = getState().api;
    const url = generateFilterUrl({
      brandsIds,
      department,
      categoryId: currentCategoryId,
      seasons,
      sizes: sizesSelected,
      sortKey: sortFilterSelect,
      productListMode,
      storeName,
      storeId
    });

    trackFilterChange(FILTER_TYPE_SIZES, splitSizes(sizesSelected), splitSizes(oldSizes));
    dispatch(callBackend(url, productListMode));
  };
}

function sort(sortKey, sortText, productListMode) {
  return (dispatch, getState) => {
    const {
      brandsIds,
      refinedBrandName,
      currentCategoryId,
      seasons,
      sizes,
      storeName,
      storeId
    } = getState().api.filterSettings;
    const { department } = getState().api;
    const url = generateFilterUrl({
      brandsIds,
      department,
      brandName: refinedBrandName,
      categoryId: currentCategoryId,
      seasons,
      sizes,
      sortKey,
      productListMode,
      storeName,
      storeId
    });
    track(TrackingEvents.productsSorted(), { ui: { sort_type: sortText } });
    dispatch(callBackend(url, productListMode));
  };
}

function changeOffset(offset, productListMode) {
  return (dispatch, getState) => {
    const {
      brandsIds,
      refinedBrandName,
      currentCategoryId,
      seasons,
      sizes,
      storeName,
      storeId
    } = getState().api.filterSettings;
    const { department } = getState().api;
    const { sortFilterSelect } = getState().api;
    const url = generateFilterUrl({
      brandsIds,
      department,
      brandName: refinedBrandName,
      categoryId: currentCategoryId,
      seasons,
      sizes,
      sortKey: sortFilterSelect,
      offset,
      productListMode,
      storeName,
      storeId
    });
    track(TrackingEvents.productsPageChanges(), { ui: { page_selected: offset / 60 + 1 } });
    dispatch(callBackend(url, productListMode));
  };
}

function clearFilters(filterTypes, productListMode) {
  return (dispatch, getState) => {
    const { currentCategoryId, storeId, storeName, brandsIds } = getState().api.filterSettings;
    const { department } = getState().api;
    const { sortFilterSelect } = getState().api;
    const url = generateFilterUrl({
      department,
      categoryId: currentCategoryId,
      sortKey: sortFilterSelect,
      storeId,
      storeName,
      productListMode,
      brandsIds: productListMode === designerMode ? brandsIds : undefined
    });
    trackFilterChange(FILTER_TYPE_CLEAR_ALL, null);
    dispatch(callBackend(url, productListMode));
  };
}

function shouldPreserveSizes(newCategoryId, oldCategoryId) {
  // toggling between shoes and everything else needs to wipe size filter since these are different scales
  return isShoesCategory(newCategoryId) === isShoesCategory(oldCategoryId);
}

export { callBackend, sort, filterBrands, filterCategories, filterSizes, filterSeasons, changeOffset, clearFilters };
